import { useGiteaRepos, useGiteaBranches } from "@/api/queries/gitea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Radix-Select erlaubt kein value="" für Items → Sentinel für "kein Seed".
const NONE = "__none__";

interface Props {
  seedRepo: string | null;
  seedBranch: string | null;
  onChange: (seedRepo: string | null, seedBranch: string | null) => void;
  // Profil: "kein Seed" erlaubt (Agent ohne Workspace). Agent-Flash (E3): false.
  allowNone?: boolean;
}

// #215 (E2): Repo+Branch-Auswahl aus Gitea. Repo-Wechsel setzt den Branch auf die
// default-Branch des neuen Repos. Wiederverwendbar (Profil-Seed + Agent-Flash).
export function RepoBranchPicker({ seedRepo, seedBranch, onChange, allowNone = true }: Props) {
  const reposQuery = useGiteaRepos();
  const branchesQuery = useGiteaBranches(seedRepo);

  function handleRepoChange(value: string) {
    if (value === NONE) {
      onChange(null, null);
      return;
    }
    const repo = reposQuery.data?.find((r) => r.fullName === value);
    onChange(value, repo?.defaultBranch ?? null);
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>Workspace-Seed-Repo (Gitea)</Label>
        <Select value={seedRepo ?? NONE} onValueChange={handleRepoChange}>
          <SelectTrigger data-testid="field-seedRepo">
            <SelectValue placeholder="Repo wählen…" />
          </SelectTrigger>
          <SelectContent>
            {allowNone && <SelectItem value={NONE}>— kein Seed (Agent ohne Workspace)</SelectItem>}
            {reposQuery.data?.map((r) => (
              <SelectItem key={r.fullName} value={r.fullName}>
                {r.fullName}
                {r.empty ? " (leer)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {reposQuery.isLoading && <div className="text-xs text-subtext0">Repos laden…</div>}
        {reposQuery.isError && (
          <div className="text-xs text-red">Gitea nicht erreichbar — Seed optional</div>
        )}
      </div>

      {seedRepo && (
        <div className="space-y-1">
          <Label>Branch</Label>
          <Select value={seedBranch ?? ""} onValueChange={(b) => onChange(seedRepo, b)}>
            <SelectTrigger data-testid="field-seedBranch">
              <SelectValue placeholder="Branch wählen…" />
            </SelectTrigger>
            <SelectContent>
              {branchesQuery.data?.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {branchesQuery.isLoading && <div className="text-xs text-subtext0">Branches laden…</div>}
        </div>
      )}
    </div>
  );
}
